import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LibraryBooks } from "@mui/icons-material";
import {
  attachToPrompt as attachClinicalToPrompt,
  deleteDocument,
  uploadDocument as uploadClinicalReference,
} from "services/clinicalReferencesService";
import { uploadDocument as uploadEmbeddedDocument } from "services/embeddedDocumentsService";
import { useAuth } from "hooks/useAuth";
import DocumentUploadZone from "./DocumentUploadZone";
import DocumentTable from "./DocumentTable";
import DocumentLibraryDialog from "./DocumentLibraryDialog";
import BulkDeleteConfirmDialog from "./BulkDeleteConfirmDialog";
import {
  compareByFileName,
  docMatchesPrompt,
  hidePendingDuplicates,
} from "./documentListUtils";

const ACCEPTED_REFERENCE_TYPES = [
  ".pdf", ".doc", ".docx", ".odf", ".odt", ".ppt", ".pptx", ".md", ".txt",
];

const ClinicalReferencesPanel = ({
  disabled = false,
  promptId = "",
  references,
  scope = "global",
}) => {
  // `references` (legacy prop) takes precedence — lets callers pin a fixed list.
  const useFixedReferenceList = references !== undefined;
  const normalizedPromptId = String(promptId || "").trim().toLowerCase();
  const hasPromptId = Boolean(normalizedPromptId);

  const { clinicalRefs: cachedClinicalRefs, chartmindDocsLoading } = useAuth();
  const documents = useMemo(
    () =>
      (cachedClinicalRefs || [])
        .filter((doc) => docMatchesPrompt(doc, normalizedPromptId))
        .sort(compareByFileName),
    [cachedClinicalRefs, normalizedPromptId],
  );
  const documentsLoading = chartmindDocsLoading;
  const documentsError = null;

  const [pendingReferences, setPendingReferences] = useState([]);
  const [externallyRemovedIds, setExternallyRemovedIds] = useState(() => new Set());
  const [uploadError, setUploadError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [deleteDialogDocs, setDeleteDialogDocs] = useState([]);

  const persistedReferences = useMemo(() => {
    const source = useFixedReferenceList ? references : documents || [];
    return source.filter((ref) => !externallyRemovedIds.has(ref.id));
  }, [useFixedReferenceList, references, documents, externallyRemovedIds]);

  // Hide a pending placeholder as soon as its uploaded row arrives via the live
  // snapshot, so a document never appears twice mid-batch.
  const visibleReferences = useMemo(
    () => [
      ...hidePendingDuplicates(pendingReferences, persistedReferences),
      ...persistedReferences,
    ],
    [pendingReferences, persistedReferences],
  );

  const normalizePendingReference = (file) => ({
    id: `pending-${file.name}-${file.size}-${file.lastModified}`,
    fileName: file.name,
    createdAt: new Date(),
    isPending: true,
  });

  const acceptFiles = async (files, includeEmbedded) => {
    if (!hasPromptId) return;
    const acceptedFiles = files.filter((file) => {
      const lowerName = file.name.toLowerCase();
      return ACCEPTED_REFERENCE_TYPES.some((extension) => lowerName.endsWith(extension));
    });
    if (!acceptedFiles.length) return;

    setUploadError(null);
    setSuccessMessage(null);
    const normalized = acceptedFiles.map(normalizePendingReference);

    setPendingReferences((current) => {
      const existing = new Set(current.map((ref) => ref.id));
      const merged = [...current];
      normalized.forEach((ref) => {
        if (!existing.has(ref.id)) merged.push(ref);
      });
      return merged;
    });

    const errors = [];
    const BATCH = 5;
    let done = 0;

    setUploading(true);
    setUploadProgress({ done: 0, total: acceptedFiles.length });

    for (let i = 0; i < acceptedFiles.length; i += BATCH) {
      await Promise.all(
        acceptedFiles.slice(i, i + BATCH).map(async (file, j) => {
          const fileIndex = i + j;
          const failedId = normalized[fileIndex].id;
          try {
            const tasks = [uploadClinicalReference(file, normalizedPromptId, {}, scope)];
            if (includeEmbedded) {
              tasks.push(uploadEmbeddedDocument(file, normalizedPromptId, {}, {}, scope));
            }
            await Promise.all(tasks);
          } catch (err) {
            console.error("[ClinicalReferencesPanel] upload failed:", file.name, err);
            errors.push(file.name);
            setPendingReferences((current) => current.filter((ref) => ref.id !== failedId));
          } finally {
            done += 1;
            setUploadProgress({ done, total: acceptedFiles.length });
          }
        }),
      );
    }

    setUploading(false);

    if (errors.length) {
      setUploadError(`Failed to upload: ${errors.join(", ")}`);
    }

    const succeeded = acceptedFiles.length - errors.length;
    if (succeeded > 0) {
      const noun = succeeded === 1 ? "reference" : "references";
      const where = includeEmbedded ? " (also added to Embedded Documents)" : "";
      setSuccessMessage(`Uploaded ${succeeded} ${noun}${where}.`);
    }


    setPendingReferences((current) =>
      current.filter((ref) => errors.some((name) => ref.fileName === name)),
    );
  };

  const handleRemove = async (reference) => {
    if (reference.isPending) {
      setPendingReferences((current) =>
        current.filter((entry) => entry.id !== reference.id),
      );
      return;
    }
    if (useFixedReferenceList) {
      // Legacy caller (QueryManagementModal) manages the list externally — just hide locally.
      setSuccessMessage(null);
      setUploadError(null);
      setExternallyRemovedIds((current) => {
        const next = new Set(current);
        next.add(reference.id);
        return next;
      });
      setSuccessMessage(`Removed ${reference.fileName || "reference"}.`);
      return;
    }
    setDeleteDialogDocs([reference]);
  };

  const toggleSelected = (docId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedDocs = useMemo(
    () => (documents || []).filter((doc) => selectedIds.has(doc.id)),
    [documents, selectedIds],
  );

  const handleBulkDeleteClick = () => {
    if (!selectedDocs.length) return;
    setDeleteDialogDocs(selectedDocs);
  };

  // Removal only detaches from this prompt; files stay in the library.
  // Permanent deletion lives on the Disk Usage page.
  const handleConfirmDelete = async () => {
    if (!deleteDialogDocs.length) return;
    setSuccessMessage(null);
    setUploadError(null);
    const ids = deleteDialogDocs.map((doc) => doc.id);
    await deleteDocument(ids, normalizedPromptId, { detachOnly: true });
    setSelectedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setSuccessMessage(
      `Removed ${ids.length} ${ids.length === 1 ? "reference" : "references"} from this prompt.`,
    );
    setDeleteDialogDocs([]);
  };

  const handleLibraryAttached = ({ attachedCount, errors }) => {
    if (attachedCount > 0) {
      setSuccessMessage(
        `Attached ${attachedCount} ${attachedCount === 1 ? "reference" : "references"} from your library.`,
      );
    }
    if (errors?.length) {
      setUploadError(
        `Some attachments failed: ${errors.map((e) => e.error || e.docId).join(", ")}`,
      );
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {!hasPromptId && !useFixedReferenceList ? (
        <Typography variant="body2" color="text.secondary">
          Enter a prompt ID before uploading clinical references.
        </Typography>
      ) : null}
      <DocumentUploadZone
        disabled={disabled}
        hasPromptId={hasPromptId || useFixedReferenceList}
        acceptedExtensions={ACCEPTED_REFERENCE_TYPES}
        inputId="clinical-references-upload"
        secondaryTarget={{
          value: "embedded",
          label: "Also add to Embedded Documents",
        }}
        onFilesAccepted={acceptFiles}
        trailing={
          hasPromptId && !useFixedReferenceList ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<LibraryBooks />}
              onClick={() => setLibraryOpen(true)}
              disabled={disabled || uploading}
            >
              Add from library
            </Button>
          ) : null
        }
      />

      {uploading ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Uploading {uploadProgress.done} / {uploadProgress.total}…
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.total > 0 ? (uploadProgress.done / uploadProgress.total) * 100 : 0}
          />
        </Box>
      ) : null}

      {uploadError ? (
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      ) : null}

      {/* minHeight reserves room for the selection buttons so the header does not shift */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", minHeight: 28 }}>
        <Typography variant="subtitle2">
          Clinical References{persistedReferences.length ? ` (${persistedReferences.length})` : ""}
        </Typography>
        {!useFixedReferenceList && selectedIds.size > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, py: 0.25 }}
              onClick={clearSelection}
              disabled={disabled}
            >
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              sx={{ px: 1, py: 0.25 }}
              onClick={handleBulkDeleteClick}
              disabled={disabled}
            >
              Remove selected
            </Button>
          </Stack>
        ) : null}
      </Box>
      <DocumentTable
        documents={visibleReferences}
        loading={documentsLoading}
        error={documentsError}
        emptyMessage="No references uploaded yet."
        disabled={disabled}
        onRemove={handleRemove}
        selection={
          useFixedReferenceList
            ? undefined
            : { selectedIds, onToggle: toggleSelected }
        }
        isRowDisabled={(doc) => Boolean(doc.isPending)}
        getSecondaryDetails={(doc) => {
          const meta = doc.metadata || {};
          const source = meta.source || doc.source;
          const specialty = meta.specialty || doc.specialty;
          return [source, specialty].filter(Boolean).join(" • ") || null;
        }}
      />

      {!useFixedReferenceList ? (
        <>
          <DocumentLibraryDialog
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            promptId={normalizedPromptId}
            title="Add references from your library"
            emptyMessage="No references in your library yet. Upload one first to make it available here."
            libraryDocuments={cachedClinicalRefs || []}
            loading={chartmindDocsLoading}
            attachToPrompt={attachClinicalToPrompt}
            onAttached={handleLibraryAttached}
          />

          <BulkDeleteConfirmDialog
            open={deleteDialogDocs.length > 0}
            documents={deleteDialogDocs}
            onClose={() => setDeleteDialogDocs([])}
            onConfirm={handleConfirmDelete}
            resourceNoun="reference"
          />
        </>
      ) : null}
    </Box>
  );
};

export default ClinicalReferencesPanel;
